import { trackConfig, type IFramework } from '@/framework';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { type TextSelectionRange } from '@/utils/text-kit';
import { LayerType, type ILayerProps, type ITextLayerProps, type Optional } from '@/version-types';
import type { IEdgelessTextLayer } from '../types';
import {
    applySnapshotSize,
    applyTextRangeAttrs,
    collectFontSubsetRequests,
    pushHistory,
    refreshFontMaterialIDs,
    refreshRuntimeFontFamilies,
    unifyTextContentsAttrs,
    updateLayer,
    validateInputLayer,
} from '../utils';
import { type IShapeText } from './shape-text';

export interface IFontFamily {
    /**
     * 设置字体
     * @param layerID - 图层 ID
     * @param fontFamily - 字体名称
     * @param options - 配置项
     *  - range: 指定选区
     * @returns 字体数据、runtime subset 和最终排版尺寸已应用的图层与 range。
     */
    setFontFamily(
        layerID: string,
        fontFamily: string,
        options?: { range?: Optional<TextSelectionRange> },
    ): Promise<{ layer: ILayerProps; range: TextSelectionRange | null } | void>;
}

const FUNC_ID = trackConfig[LayerType.TEXT_LAYER].family;

export class FontFamily implements IFontFamily {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private async prepareFontSubsets(layer: ILayerProps): Promise<boolean> {
        const requests = collectFontSubsetRequests(layer);
        if (requests.length === 0) return true;

        const results = await Promise.all(
            requests.map(async (request) => {
                const { fontFamily, chars } = request;
                if (!this.framework.service.font.getByFamily(fontFamily)) return false;
                const subsetFamily = await this.framework.service.font.loadSubset(fontFamily, chars);
                if (!subsetFamily) return false;

                setLayerRuntimeValue(layer, {
                    fontFamilyMap: {
                        ...(layer.__runtime?.fontFamilyMap || {}),
                        [fontFamily]: subsetFamily,
                    },
                });
                return true;
            }),
        );

        return results.every(Boolean);
    }

    private async prepare(prevLayer: ILayerProps, fontFamily: string, range: TextSelectionRange | null) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        let nextLayerData: ITextLayerProps = { ...prevLayerData };
        let nextRange: TextSelectionRange | null = range;

        if (range && range.start !== range.end) {
            const result = applyTextRangeAttrs(nextLayerData, range, {
                key: 'fontFamily',
                value: fontFamily,
            });
            nextRange = result.range;
            nextLayerData = { ...nextLayerData, textContents: result.textContents };
        } else {
            nextLayerData = { ...nextLayerData, fontFamily };

            const { textContents: prevTextContents = [] } = nextLayerData;
            if (prevTextContents.length > 0) {
                const nextTextContents = unifyTextContentsAttrs(prevTextContents, { fontFamily });
                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            }
        }

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };
        nextLayer = refreshFontMaterialIDs(this.framework.service.font, nextLayer);
        nextLayer = refreshRuntimeFontFamilies(nextLayer);

        const ready = await this.prepareFontSubsets(nextLayer);
        if (!ready) return null;

        const { shapeExtension } = prevLayerData;
        if (shapeExtension) return { layer: nextLayer, range: nextRange };

        const object = this.framework.service.edgelessCanvas.getByLayerId(nextLayer.id) as IEdgelessTextLayer | null;
        const resolution = await object?.layoutAsync(nextLayer);
        // 字体命令只提交依赖等待后仍属于当前候选的 ready 几何。
        if (resolution?.status !== 'ready') return null;
        const snapshot = resolution.value;

        nextLayer = applySnapshotSize(nextLayer, snapshot);

        return {
            layer: nextLayer,
            range: nextRange,
        };
    }

    private async setGroup(prevLayer: ILayerProps, fontFamily: string) {
        if (prevLayer.children.length === 0) return;

        this.framework.service.loading.add(prevLayer.id);

        // TODO: 批量修改子文本图层文字的子集加载方式可以优化（统一按照字体收集）

        try {
            const children: ILayerProps[] = [];

            for (const child of prevLayer.children) {
                const result = await this.prepare(child, fontFamily, null);
                if (!result) return { layer: prevLayer, range: null };

                children.push(result.layer);
            }

            const nextLayer = { ...prevLayer, children };
            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, prevLayer, nextLayer, FUNC_ID);

            return { layer: nextLayer, range: null };
        } finally {
            this.framework.service.loading.remove(prevLayer.id);
        }
    }

    private async setSingle(prevLayer: ILayerProps, fontFamily: string, range: TextSelectionRange | null) {
        this.framework.service.loading.add(prevLayer.id);

        try {
            const result = await this.prepare(prevLayer, fontFamily, range);
            if (!result) return { layer: prevLayer, range };

            let nextLayer = result.layer;

            const { shapeExtension } = prevLayer.data as ITextLayerProps;
            if (shapeExtension) {
                nextLayer = await this.shapeText.refreshShapeTextRender(nextLayer);
            }

            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, prevLayer, nextLayer, FUNC_ID);

            return { layer: nextLayer, range: result.range };
        } finally {
            this.framework.service.loading.remove(prevLayer.id);
        }
    }

    async setFontFamily(layerID: string, fontFamily: string, options: { range?: Optional<TextSelectionRange> } = {}) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            return this.setGroup(prevLayer, fontFamily);
        }

        return this.setSingle(prevLayer, fontFamily, options.range ?? null);
    }
}
